import client from './apiClient';

// ─── SUPPORT TICKETS (End User + Admin) ──────────────────────────────

// Get all tickets (admin - with filters)
const getTickets = (params = {}) => {
  const query = new URLSearchParams({
    ...(params.status && { status: params.status }),
    ...(params.priority && { priority: params.priority }),
    ...(params.category && { category: params.category }),
    ...(params.search && { search: params.search }),
    page: params.page || 1,
    pageSize: params.pageSize || 20,
    ...(params.sortBy && { sortBy: params.sortBy }),
    ...(params.sortOrder && { sortOrder: params.sortOrder }),
  }).toString();
  return client.get(`/support/tickets?${query}`);
};

// Get tickets for current user (end user)
const getMyTickets = () => client.get('/support/tickets/my');

// Get single ticket by ID
const getTicketById = (ticketId) => client.get(`/support/tickets/${ticketId}`);

// Create new ticket (end user or admin)
const createTicket = (data) => client.post('/support/tickets', JSON.stringify(data));
// data: { title, description, category, priority, attachments[], session_id, browser, os, device, ip_address, location, screen_resolution }

// Update ticket status/assignment (admin)
const updateTicket = (ticketId, data) => client.put(`/support/tickets/${ticketId}`, JSON.stringify(data));
// data: { status, priority, assigned_agent_id, assigned_agent_name }

// Delete ticket (soft delete - admin)
const deleteTicket = (ticketId) => client.delete(`/support/tickets/${ticketId}`);

// ─── TICKET COMMENTS ─────────────────────────────────────────────────

// Get comments for a ticket
const getTicketComments = (ticketId) => client.get(`/support/tickets/${ticketId}/comments`);

// Add comment to a ticket
const addComment = (ticketId, data) => client.post(`/support/tickets/${ticketId}/comments`, JSON.stringify(data));
// data: { message, is_internal_note, attachments[] }

// ─── FILE UPLOAD ─────────────────────────────────────────────────────

// Upload attachment for a ticket
const uploadAttachment = (formData) => client.post('/support/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});

// ─── ANALYTICS (Admin Dashboard) ────────────────────────────────────

// Get dashboard metrics (KPIs), scoped to a fromDate/toDate range (YYYY-MM-DD)
const getDashboardMetrics = (fromDate, toDate) => {
  const query = new URLSearchParams({ fromDate, toDate }).toString();
  return client.get(`/support/analytics/metrics?${query}`);
};
// Get recent session recordings across all users (not scoped to a ticket).
// Pass `cursor` (the nextCursor from a previous response) to page through the full result set.
const getAllSessions = (limit, cursor) => {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit);
  if (cursor) params.set('cursor', cursor);
  const query = params.toString();
  return client.get(`/support/analytics/sessions${query ? `?${query}` : ''}`);
};

// Get an iframe-embeddable URL for a specific session recording (real video playback)
const getReplayEmbedUrl = (sessionId) => client.get(`/support/analytics/replay-embed?sessionId=${sessionId}`);

// Get error logs
const getErrorLogs = (params = {}) => {
  const query = new URLSearchParams({
    ...(params.status && { status: params.status }),
    ...(params.search && { search: params.search }),
    page: params.page || 1,
    pageSize: params.pageSize || 20,
  }).toString();
  return client.get(`/support/analytics/errors?${query}`);
};
// Get user's event flow (for ticket inspection)
const getUserEventFlow = (userId, sessionId) => {
  const query = new URLSearchParams({
    userId,
    ...(sessionId && { sessionId }),
  }).toString();
  return client.get(`/support/analytics/user-flow?${query}`);
};

// Get all sessions for a user
const getUserSessions = (userId) => client.get(`/support/analytics/user-sessions?userId=${userId}`);

// Get the catalog of distinct events captured, with counts/reach, over a fromDate/toDate range
const getEventCatalog = (fromDate, toDate) => {
  const query = new URLSearchParams({ fromDate, toDate }).toString();
  return client.get(`/support/analytics/events?${query}`);
};
// Get the users who fired a specific event, over a fromDate/toDate range
const getEventUsers = (eventName, fromDate, toDate, limit = 100) => {
  const query = new URLSearchParams({ event: eventName, fromDate, toDate, limit }).toString();
  return client.get(`/support/analytics/event-users?${query}`);
};

// ─── POSTHOG PROXY (to avoid exposing personal API key in frontend) ──

// Get PostHog events for a user (proxied through backend)
const getPosthogEvents = (distinctId, params = {}) => {
  const query = new URLSearchParams({
    distinctId,
    ...(params.after && { after: params.after }),
    ...(params.before && { before: params.before }),
    ...(params.limit && { limit: params.limit }),
  }).toString();
  return client.get(`/support/posthog/events?${query}`);
};

// Get PostHog session recordings for a user
const getPosthogSessions = (distinctId) => client.get(`/support/posthog/sessions?distinctId=${distinctId}`);

// Get PostHog dashboard insights (DAU, sessions, etc.)
const getPosthogInsights = (insightType) => client.get(`/support/posthog/insights?type=${insightType}`);

const supportServices = {
  // Tickets
  getTickets,
  getMyTickets,
  getTicketById,
  createTicket,
  updateTicket,
  deleteTicket,
  // Comments
  getTicketComments,
  addComment,
  // Upload
  uploadAttachment,
  // Analytics
  getDashboardMetrics,
  getAllSessions,
  getReplayEmbedUrl,
  getErrorLogs,
  getUserEventFlow,
  getUserSessions,
  getEventCatalog,
  getEventUsers,
  // PostHog proxy
  getPosthogEvents,
  getPosthogSessions,
  getPosthogInsights,
};

export default supportServices;
